import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import CircularProgress from '@mui/material/CircularProgress';
import { surface, tone } from '../timeclock/tcFormat';
import {
    DEPARTMENTS, KINDS_LEGEND, addDays, departmentLabel, hours, isoDate, isoWeekday, kindColor, kindLabel,
    locale, mondayOf, parseDate, rosterDay, schoolHolidayLabel, schoolHolidayOn, specialLabel, teamError, teamPost,
} from './teamFormat';

const NAME_W = 150;
const SIZES = { week: { w: 104, h: 40 }, month: { w: 30, h: 30 }, year: { w: 11, h: 24 } };
const PAINT_KINDS = ['LEAVE', 'RECUP', 'SICK', 'SPECIAL', 'REPLACEMENT', 'INSTALL', 'CLEAR'];
const SPECIAL_CODES = ['KLEIN_VERLET', 'GEBOORTE', 'ADOPTIE', 'OUDERSCHAP', 'FAMILIAAL', 'ZORG', 'MANTELZORG', 'OPLEIDING', 'ONBETAALD', 'ANDERE'];

/**
 * Team board: one row per colleague, grouped by department, one column per
 * day, in a week, month or year view. Weekends and holidays are grey, days
 * off by roster hatched, home working ⌂, weekend duty a dot. Below
 * development and support the number present (Kristof counts for
 * development). Management can paint days directly.
 */
const TeamCalendarTab = ({ t, lang, theme, token, me, staff, reloadKey, failed, notify }) => {
    const today = me.today || isoDate(new Date());
    const [view, setView] = useState(() => {
        try { return window.sessionStorage.getItem('team_view') || 'month'; } catch (e) { return 'month'; }
    });
    const [anchor, setAnchor] = useState(today);
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [again, setAgain] = useState(0);
    const [paint, setPaint] = useState(null);
    const [special, setSpecial] = useState('KLEIN_VERLET');
    const [remark, setRemark] = useState('');
    const [painting, setPainting] = useState(false);

    const changeView = (v) => {
        if (!v) return;
        try { window.sessionStorage.setItem('team_view', v); } catch (e) { /* private mode */ }
        setView(v);
    };

    const days = useMemo(() => {
        let from;
        let count;
        if (view === 'week') {
            from = mondayOf(anchor);
            count = 7;
        } else if (view === 'year') {
            from = anchor.substring(0, 4) + '-01-01';
            count = Math.round((parseDate(anchor.substring(0, 4) + '-12-31') - parseDate(from)) / 86400000) + 1;
        } else {
            from = anchor.substring(0, 7) + '-01';
            const d = parseDate(from);
            count = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        }
        const out = [];
        for (let i = 0; i < count; i++) out.push(addDays(from, i));
        return out;
    }, [view, anchor]);

    useEffect(() => {
        let alive = true;
        setBusy(true);
        teamPost('calendar', token, { from: days[0], to: days[days.length - 1] }).then((response) => {
            if (!alive) return;
            setBusy(false);
            if (response?.success) {
                setData(response.data);
                setError('');
            } else {
                setError(failed(response));
            }
        });
        return () => { alive = false; };
    }, [token, days, reloadKey, again, failed]);

    const shift = (delta) => {
        const d = parseDate(anchor);
        if (view === 'week') d.setDate(d.getDate() + 7 * delta);
        else if (view === 'year') d.setFullYear(d.getFullYear() + delta);
        else { d.setDate(1); d.setMonth(d.getMonth() + delta); }
        setAnchor(isoDate(d));
    };

    const index = useMemo(() => {
        const holidays = {};
        const entries = {};
        const home = {};
        const duty = {};
        (data?.holidays || []).forEach((h) => { holidays[h.day] = h; });
        (data?.entries || []).forEach((e) => { (entries[e.staff_id + '|' + e.day] = entries[e.staff_id + '|' + e.day] || []).push(e); });
        (data?.home || []).forEach((h) => { home[h.staff_id + '|' + h.day] = true; });
        (data?.duty || []).forEach((d) => { duty[d.staff_id + '|' + d.day] = true; });
        return { holidays, entries, home, duty };
    }, [data]);

    const s = surface(theme);
    const dark = theme === 'dark';
    const size = SIZES[view];
    const grey = dark ? 'rgba(255,255,255,0.06)' : '#EEF1F5';
    const hatch = 'repeating-linear-gradient(135deg, transparent 0 4px, ' + (dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)') + ' 4px 5px)';
    const dutyColor = dark ? '#9C7FE0' : '#7A56C2';
    // School holidays (Flemish Community): a very light green behind the day headers.
    const schoolColor = dark ? 'rgba(125,187,95,0.16)' : 'rgba(106,168,79,0.13)';
    const loc = locale(lang);
    const closed = (day) => isoWeekday(day) >= 6 || !!index.holidays[day];

    const title = view === 'year' ? anchor.substring(0, 4)
        : view === 'week' ? parseDate(days[0]).toLocaleDateString(loc, { day: 'numeric', month: 'short' }) + ' – '
            + parseDate(days[6]).toLocaleDateString(loc, { day: 'numeric', month: 'short', year: 'numeric' })
            : parseDate(days[0]).toLocaleDateString(loc, { month: 'long', year: 'numeric' });

    const paintDay = useCallback((person, day) => {
        if (!paint || painting) return;
        if (paint === 'SPECIAL' && !remark.trim()) {
            notify(t('team_err_remark', 'A remark is required for special leave.'), false);
            return;
        }
        setPainting(true);
        teamPost('paint', token, { staff_id: person.id, day, kind: paint, special_code: paint === 'SPECIAL' ? special : null, remark: paint === 'SPECIAL' ? remark : '' })
            .then((response) => {
                setPainting(false);
                if (response?.success) setAgain((k) => k + 1);
                else notify(teamError(t, response), false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paint, painting, special, remark, token]);

    const cellLabel = (e) => {
        if (e.kind === 'AWAY') return kindLabel(t, 'AWAY');
        if (e.kind === 'SPECIAL' && e.special_code) return specialLabel(t, e.special_code);
        if (e.kind === 'INSTALL' && e.client) return e.client;
        return kindLabel(t, e.kind);
    };

    const cell = (person, day) => {
        const weekend = isoWeekday(day) >= 6;
        const holiday = index.holidays[day];
        const list = index.entries[person.id + '|' + day] || [];
        const off = !weekend && !holiday && !rosterDay(person.roster, day);
        const home = index.home[person.id + '|' + day];
        const duty = index.duty[person.id + '|' + day];
        let background = weekend || holiday ? grey : off ? hatch : 'transparent';
        let border = 'none';
        let text = '';
        const tip = [];
        const when = parseDate(day).toLocaleDateString(loc, { weekday: 'long', day: 'numeric', month: 'long' });
        if (list.length) {
            const e = list[0];
            const color = kindColor(e.kind, theme);
            const pending = e.status === 'PENDING';
            background = pending ? 'repeating-linear-gradient(135deg, ' + color + '55 0 4px, transparent 4px 8px)' : color;
            border = pending ? '1.5px solid ' + color : 'none';
            text = cellLabel(e);
            list.forEach((x) => {
                tip.push(cellLabel(x)
                    + (x.hours !== undefined && x.kind !== 'SPECIAL' ? ' · ' + hours(x.hours, lang) : '')
                    + (x.status === 'PENDING' ? ' · ' + t('team_status_pending', 'requested') : '')
                    + (x.source === 'MANAGEMENT' ? ' · ' + t('team_by_management', 'entered by management') : '')
                    + (x.remark ? ' · "' + x.remark + '"' : ''));
            });
        } else if (holiday) {
            tip.push(holiday.name);
        } else if (off) {
            tip.push(t('team_legend_off', 'Day off by roster'));
        } else if (home && !weekend) {
            tip.push(t('team_home', 'Works from home'));
            text = '⌂ ' + t('team_home_short', 'home');
        }
        if (duty) {
            tip.push(t('team_weekend_duty', 'Weekend duty'));
            if (!text) text = t('team_weekend_duty', 'Weekend duty');
        }
        const paintable = paint && me.rights.paint && !weekend && !holiday && !off;
        const box = (
            <Box onClick={paintable ? () => paintDay(person, day) : undefined}
                sx={{ width: size.w, height: size.h, flexShrink: 0, borderLeft: view === 'year' ? 'none' : s.line, p: view === 'year' ? '2px 0.5px' : '3px',
                    boxSizing: 'border-box', position: 'relative', cursor: paintable ? 'crosshair' : 'default',
                    borderLeftStyle: view === 'year' && day.endsWith('-01') ? 'solid' : undefined,
                    borderLeftWidth: view === 'year' && day.endsWith('-01') ? 1 : undefined,
                    borderLeftColor: view === 'year' && day.endsWith('-01') ? (dark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)') : undefined,
                    backgroundColor: day === today ? (dark ? 'rgba(0,113,187,0.18)' : 'rgba(0,113,187,0.07)') : undefined,
                    '&:hover': paintable ? { outline: '2px solid ' + (paint === 'CLEAR' ? tone.muted : kindColor(paint, theme)), outlineOffset: -2 } : undefined }}>
                <Box sx={{ width: '100%', height: '100%', borderRadius: view === 'year' ? '1px' : '4px', background, border, boxSizing: 'border-box',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {view === 'week' && text && (
                        <Box component="span" sx={{ fontSize: 11.5, fontWeight: 600, px: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            color: list.length && list[0].status !== 'PENDING' ? '#fff' : 'text.secondary' }}>{text}</Box>
                    )}
                    {view === 'month' && !list.length && home && !weekend && (
                        <Box component="span" sx={{ fontSize: 13, color: s.faint, lineHeight: 1 }}>⌂</Box>
                    )}
                </Box>
                {duty && view !== 'week' && (
                    <Box sx={{ position: 'absolute', width: view === 'year' ? 5 : 8, height: view === 'year' ? 5 : 8, borderRadius: '50%', backgroundColor: dutyColor,
                        top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                )}
            </Box>
        );
        if (!tip.length || view === 'year' && !list.length && !duty) return <React.Fragment key={day}>{box}</React.Fragment>;
        return (
            <Tooltip key={day} disableInteractive title={
                <Box sx={{ fontSize: 12.5, lineHeight: 1.5 }}><b>{person.name}</b> · {when}{tip.map((x, i) => <div key={i}>{x}</div>)}</Box>
            }>{box}</Tooltip>
        );
    };

    // Occupancy rows: development at least 1 present (Kristof counts), support at most 2 away.
    const coverRow = (kind) => {
        const members = staff.filter((p) => p.coverage === kind);
        if (!members.length) return null;
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', borderTop: s.line, backgroundColor: dark ? 'rgba(255,255,255,0.02)' : '#FAFBFC' }}>
                <Box sx={{ width: NAME_W, flexShrink: 0, px: 1.5, fontSize: 12, color: s.faint, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {kind === 'DEV' ? t('team_cover_dev', 'Dev present') : t('team_cover_support', 'Support present')}
                </Box>
                {days.map((day) => {
                    if (closed(day)) return <Box key={day} sx={{ width: size.w, flexShrink: 0, height: 22, borderLeft: view === 'year' ? 'none' : s.line }} />;
                    const working = members.filter((p) => rosterDay(p.roster, day));
                    const away = working.filter((p) => (index.entries[p.id + '|' + day] || []).length > 0).length;
                    const present = working.length - away;
                    const color = kind === 'DEV'
                        ? (present < 1 ? tone.bad : tone.ok)
                        : (away > 2 ? tone.bad : away === 2 ? tone.warn : tone.ok);
                    return (
                        <Tooltip key={day} disableInteractive title={present + ' ' + t('team_present', 'present') + ', ' + away + ' ' + t('team_away_count', 'away')}>
                            <Box sx={{ width: size.w, flexShrink: 0, height: 22, borderLeft: view === 'year' ? 'none' : s.line, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 11.5, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums', backgroundColor: view === 'year' ? color + '33' : undefined }}>
                                {view === 'year' ? '' : present}
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>
        );
    };

    const groups = DEPARTMENTS.map((dept) => ({ dept, people: staff.filter((p) => p.department === dept) })).filter((g) => g.people.length);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <IconButton size="small" onClick={() => shift(-1)} aria-label={t('team_prev', 'Previous')}><ChevronLeft /></IconButton>
                <Typography sx={{ fontSize: 16, fontWeight: 700, minWidth: 170, textAlign: 'center', textTransform: 'capitalize' }}>{title}</Typography>
                <IconButton size="small" onClick={() => shift(1)} aria-label={t('team_next', 'Next')}><ChevronRight /></IconButton>
                {!days.includes(today) && (
                    <Button size="small" onClick={() => setAnchor(today)} sx={{ textTransform: 'none' }}>{t('team_today', 'Today')}</Button>
                )}
                {(busy || painting) && <CircularProgress size={16} sx={{ ml: 1 }} />}
                <Box sx={{ flex: 1 }} />
                <ToggleButtonGroup size="small" exclusive value={view} onChange={(e, v) => changeView(v)}>
                    <ToggleButton value="week" sx={{ textTransform: 'none', px: 1.5 }}>{t('team_view_week', 'Week')}</ToggleButton>
                    <ToggleButton value="month" sx={{ textTransform: 'none', px: 1.5 }}>{t('team_view_month', 'Month')}</ToggleButton>
                    <ToggleButton value="year" sx={{ textTransform: 'none', px: 1.5 }}>{t('team_view_year', 'Year')}</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {me.rights.paint && (
                <Box sx={{ ...s.card, p: 1.25, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: s.faint, mr: 0.5 }}>{t('team_paint', 'Fill in the calendar:')}</Typography>
                    {PAINT_KINDS.map((k) => {
                        const on = paint === k;
                        return (
                            <Button key={k} size="small" variant={on ? 'contained' : 'outlined'} disableElevation onClick={() => setPaint(on ? null : k)}
                                sx={{ textTransform: 'none', height: 30, px: 1.25, fontSize: 12.5,
                                    ...(on ? { backgroundColor: k === 'CLEAR' ? tone.muted : kindColor(k, theme), color: k === 'REPLACEMENT' ? '#1a1a1a' : '#fff',
                                        '&:hover': { backgroundColor: k === 'CLEAR' ? tone.muted : kindColor(k, theme) } } : { color: 'text.primary', borderColor: s.ghost }) }}
                                startIcon={k === 'CLEAR' ? null : <Box component="span" sx={{ width: 10, height: 10, borderRadius: '3px', backgroundColor: kindColor(k, theme), border: on ? '1px solid #fff' : 'none' }} />}>
                                {k === 'CLEAR' ? t('team_paint_clear', 'Clear') : kindLabel(t, k)}
                            </Button>
                        );
                    })}
                    {paint === 'SPECIAL' && (
                        <>
                            <TextField select size="small" value={special} onChange={(e) => setSpecial(e.target.value)} sx={{ minWidth: 220, '& .MuiInputBase-root': { height: 32, fontSize: 13 } }}>
                                {SPECIAL_CODES.map((c) => <MenuItem key={c} value={c}>{specialLabel(t, c)}</MenuItem>)}
                            </TextField>
                            <TextField size="small" value={remark} onChange={(e) => setRemark(e.target.value)} placeholder={t('team_remark_required', 'Remark (required)')}
                                sx={{ minWidth: 220, '& .MuiInputBase-root': { height: 32, fontSize: 13 } }} />
                        </>
                    )}
                    {paint && (
                        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', ml: 0.5 }}>
                            {paint === 'CLEAR' ? t('team_paint_hint_clear', 'Tap the days that must be empty.') : t('team_paint_hint', 'Tap the days that get this colour; approved at once.')}
                        </Typography>
                    )}
                </Box>
            )}

            {error && <Typography sx={{ color: tone.bad, fontSize: 13.5, mb: 1 }}>{error}</Typography>}

            <Box sx={{ ...s.card, overflowX: 'auto' }}>
                <Box sx={{ minWidth: NAME_W + size.w * days.length }}>
                    <Box sx={{ display: 'flex', borderBottom: s.line }}>
                        <Box sx={{ width: NAME_W, flexShrink: 0 }} />
                        {days.map((day) => {
                            const d = parseDate(day);
                            const muted = closed(day);
                            const color = day === today ? 'primary.main' : muted ? s.faint : 'text.primary';
                            if (view === 'year') {
                                return (
                                    <Box key={day} sx={{ width: size.w, flexShrink: 0, position: 'relative', height: 30 }}>
                                        {d.getDate() === 1 && (
                                            <Box sx={{ position: 'absolute', left: 2, top: 7, fontSize: 11.5, fontWeight: 700, color: s.faint, whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
                                                {d.toLocaleDateString(loc, { month: 'short' })}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            }
                            const school = schoolHolidayOn(day);
                            const head = (
                                <Box key={day} sx={{ width: size.w, flexShrink: 0, textAlign: 'center', py: 0.5, borderLeft: s.line, color, fontWeight: day === today ? 700 : 500,
                                    backgroundColor: school ? schoolColor : undefined }}>
                                    <Box sx={{ fontSize: 10.5, lineHeight: 1.2, textTransform: 'uppercase' }}>
                                        {d.toLocaleDateString(loc, { weekday: view === 'week' ? 'short' : 'narrow' })}
                                    </Box>
                                    <Box sx={{ fontSize: 12.5, lineHeight: 1.3, fontVariantNumeric: 'tabular-nums' }}>
                                        {view === 'week' ? d.toLocaleDateString(loc, { day: 'numeric', month: 'short' }) : d.getDate()}
                                    </Box>
                                </Box>
                            );
                            return school
                                ? <Tooltip key={day} disableInteractive title={schoolHolidayLabel(t, school)}>{head}</Tooltip>
                                : head;
                        })}
                    </Box>
                    {groups.map((g) => (
                        <React.Fragment key={g.dept}>
                            <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5, fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: s.faint }}>
                                {departmentLabel(t, g.dept)}
                            </Box>
                            {g.people.map((p) => (
                                <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', borderTop: s.line }}>
                                    <Box sx={{ width: NAME_W, flexShrink: 0, px: 1.5, fontSize: 13.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                        fontWeight: p.id === me.staff.id ? 700 : 400 }}>
                                        {p.name}
                                        {p.coverage === 'DEV' && p.department !== 'DEV' && <Box component="span" sx={{ color: s.faint, fontSize: 11.5 }}> · dev</Box>}
                                    </Box>
                                    {days.map((day) => cell(p, day))}
                                </Box>
                            ))}
                            {g.dept === 'DEV' && coverRow('DEV')}
                            {g.dept === 'SUPPORT' && coverRow('SUPPORT')}
                        </React.Fragment>
                    ))}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2.5, rowGap: 1, mt: 1.5, fontSize: 12.5, color: 'text.secondary', alignItems: 'center' }}>
                {KINDS_LEGEND.filter((k) => k !== 'AWAY' || !me.rights.sees_all).map((k) => (
                    <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: kindColor(k, theme) }} />
                        {k === 'AWAY' ? t('team_legend_away', 'Away (colleague)') : kindLabel(t, k)}
                    </Box>
                ))}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 14, height: 14, borderRadius: '3px', border: '1.5px solid ' + kindColor('LEAVE', theme), background: 'repeating-linear-gradient(135deg, ' + kindColor('LEAVE', theme) + '55 0 3px, transparent 3px 6px)', boxSizing: 'border-box' }} />
                    {t('team_legend_pending', 'Requested')}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: dutyColor }} /></Box>
                    {t('team_weekend_duty', 'Weekend duty')}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box component="span" sx={{ width: 14, textAlign: 'center' }}>⌂</Box>
                    {t('team_home', 'Works from home')}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: grey }} />
                    {t('team_legend_closed', 'Weekend / holiday')}
                </Box>
                {view !== 'year' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: schoolColor, border: s.line, boxSizing: 'border-box' }} />
                        {t('team_legend_school', 'School holidays (Flanders)')}
                    </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 14, height: 14, borderRadius: '3px', background: hatch, border: s.line, boxSizing: 'border-box' }} />
                    {t('team_legend_off', 'Day off by roster')}
                </Box>
            </Box>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1 }}>
                {me.rights.sees_all
                    ? t('team_vis_all', 'Management and Els see every colour, sickness and special leave included.')
                    : t('team_vis_staff', 'You see your own row in detail; of colleagues you only see that they are away, in one colour.')}
            </Typography>
        </Box>
    );
};

export default TeamCalendarTab;
